'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { logout } from '@/features/authentication/actions/logoutAction';
import {
  clearClientSessionState,
  removeHostOnlyAuthCookies,
} from '@/lib/auth/clearClientSessionState';
import { loginRouteForRoles } from '@/lib/auth/loginRoute';
import { broadcastSignOut } from '@/lib/auth/sessionBroadcast';
import { useAppUser } from '@/lib/context/AppUserContext';
import { getCookieDomainForHostname } from '@/lib/supabase/cookieDomain';
import { redirectToLogin } from '@/lib/url';

type UseSignOutResult = {
  /** Clears the client caches, ends the server session, then hard-navigates to login. */
  signOut: () => Promise<void>;
  /**
   * True from the moment `signOut` is called. It stays true on success, because the
   * document is being replaced and there is nothing left to reset. It returns to
   * false only when the server call failed and we stayed on the page.
   */
  isSigningOut: boolean;
};

/**
 * The single sign-out entry point for the app.
 *
 * Why it exists: the TanStack QueryClient is a module-scope singleton
 * (`src/providers/query-provider.tsx`) that survives every client-side navigation,
 * and no query key carries a tenant segment. Signing out without tearing it down
 * leaves the previous user's rows readable — by whoever signs in next, and by the
 * back button in the same tab.
 *
 * Why the caches are cleared AFTER the server round-trip, not before: clearing while
 * the session is still live does not hold. `queryClient.clear()` empties the cache, but
 * every mounted `useQuery` rebuilds its observer on the next render, and an empty query
 * is stale, so it refetches. `setIsSigningOut(true)` triggers exactly that render, so a
 * pre-clear repopulates itself with the outgoing tenant's rows before `logout()` even
 * returns (verified against React Query v5). Clearing once the session is dead means any
 * refetch it provokes comes back empty.
 *
 * Why the navigation is hard: `queryClient.clear()` empties the cache but does not
 * blank what mounted observers already render — they keep their last snapshot until
 * they refetch or unmount. Only replacing the document removes the pixels, discards
 * the Next.js router cache, and re-runs AppUserServerProvider with cleared cookies.
 * Do not "simplify" this back to `router.replace`: that reopens the bug even with the
 * clear in place.
 *
 * Requires both AppUserProvider and QueryClientProvider. The root layout nests them
 * in that order around the whole app, so this holds at every call site.
 */
export function useSignOut(): UseSignOutResult {
  const queryClient = useQueryClient();
  const { user } = useAppUser();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const signOut = useCallback(async () => {
    setIsSigningOut(true);

    // Read the roles before clearing anything: the context still holds the user who
    // is on their way out, which is exactly whose login page we need.
    const roles = user?.role ?? [];
    const loginRoute = loginRouteForRoles(roles);

    try {
      await logout();
    } catch (error) {
      // logout() throws rather than swallowing GoTrue's error, and that error does not
      // say whether the session is live (network failure) or dead (the refresh token was
      // already rotated, so auth-js gave up before it could remove the session). Leave
      // the caches alone either way: clearing while the session may be live would blank
      // the UI without any security benefit, and a re-render would just refetch.
      //
      // The host-only cookie sweep is safe in both cases. On hosts with a shared cookie
      // domain every legitimate writer has written the session domain-scoped since 1.26,
      // so a host-only copy is legacy residue, and it is precisely what keeps a dead
      // session "alive" on the device (sc-536): skipping the sweep here left those users
      // with no self-service exit.
      console.error('Sign-out failed:', error);
      if (getCookieDomainForHostname(window.location.hostname)?.startsWith('.')) {
        removeHostOnlyAuthCookies();
      }
      setIsSigningOut(false);
      return;
    }

    // The session is dead from here on, so nothing this drops can be refetched.
    clearClientSessionState(queryClient);
    // Legacy host-only copies of the session cookie: the server action cannot delete
    // them, its cookie store would replace Supabase's Domain-scoped delete with that one.
    removeHostOnlyAuthCookies();
    // Before navigating: this document is about to be torn down, and the other tabs
    // would otherwise keep rendering this user's data until their token refresh fails.
    broadcastSignOut(loginRoute);
    redirectToLogin(loginRoute);
  }, [queryClient, user?.role]);

  return { signOut, isSigningOut };
}
